
import { createAction } from 'redux-starter-kit';
import { post, get } from '../../../utility/via-fetch';
import { requestStart, requestFailed, requestSucceded } from '../../domain/requests/RequestActions';
import { putUser } from '../../domain/users/userActions';
import { addMessage } from './messageUiActions';
import { resetViaState } from '../../viaActions';
import { putAppConfig } from '../../domain/appConfig/appConfigActions';
import { selectSessionUser } from './sessionUiReducer';

/**
 * The id for the notification generated on sign-out.
 * We use this as a constant so we can later clear this message.
 */
export const SIGN_OUT_NOTIFICATION_ID = 'SIGN_OUT_NOTIFICATION';


/**
 * An action signifying a session user has been determined.
 * @param {string} username The identifying username of the session user.
 * @example dispatch(setSessionUser(username));
 */
export const setSessionUser = createAction('SET_SESSION_USER');


/**
 * An action signifying a signout request has been made.
 * @param {string} requestId The id of the signout request.
 * @example dispatch(fetchSignOut(requestId))
 */
export const fetchSignOut = createAction('FETCH_SIGN_OUT');

/**
 * An action that calls the server to signout the current user session.
 * @example dispatch(requestSignOut(requestId))
 */
export function requestSignOut() {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchSignOut.toString()));
    dispatch(fetchSignOut(requestId));
    try {
      await post('/api/security/sign-out');
      dispatch(requestSucceded(requestId));
      dispatch(resetViaState());
      dispatch(addMessage('success', 'sessionEnded'));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * An action that signifies a fetchSessionUser request has started.
 * @param {string} requestId The id of the request.
 * @example dispatch(fetchSessionUser(requestId))
 */
export const fetchSessionUser = createAction('FETCH_SESSION_USER');

/**
 * An action that calls the server for the current user.
 * This get the user linked to the cookie session.
 * @example const sessionUser = dispatch(requestCurrentSessionUser());
 */
export function requestCurrentSessionUser() {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchSessionUser.toString()));
    dispatch(fetchSessionUser(requestId));

    try {
      // The object contains a user due to its legacy structure.
      const user = await get('/api/security/current-user');
      dispatch(putUser(user)); // Update the domain state with our newly loaded user.
      dispatch(setSessionUser(user.userId));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(setSessionUser(null));
      dispatch(requestFailed(requestId, error));
    }
  };
}

export const fetchLanguages = createAction('FETCH_LANGUAGES');

export function requestSupportedLanguages() {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchLanguages.toString()));
    dispatch(fetchLanguages(requestId));

    try {
      // The object contains a user due to its legacy structure.
      const languages = await get('/api/application-configurations/languages', undefined, {});
      dispatch(putAppConfig({ applicationConfigCode: 'languages', languages })); // Update the domain state with our newly loaded user.
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed());
    }
  };
}

export function setSessionUserTokens(tokensAvailable) {
  return async (dispatch, getState) => {
    const user = selectSessionUser(getState());
    const { userGroup } = user;

    dispatch(putUser({ ...user, userGroup: {...userGroup, tokensAvailable } }));
  };
}
